import { useCallback, useRef, useState } from "react";
import { getPathDatabaseByRole } from "../actions/auth";
import { useDispatchApp } from "../lib/redux";
import { isAgencyWithImageAnimationPromptModal } from "../utils/animateImageConstants";

/**
 * Manages open/close state for the animate-image preview modal and delegates
 * confirmation to the caller. Only whitelisted agencies see the prompt modal;
 * others animate immediately without a user prompt.
 */
export const useAnimateImageWithPreview = () => {
  const dispatch = useDispatchApp();
  const [previewState, setPreviewState] = useState({
    isOpen: false,
    imageUrl: null,
  });
  const onConfirmRef = useRef(null);

  const openAnimatePreview = useCallback(({ imageUrl, onConfirm }) => {
    if (!imageUrl) return;
    onConfirmRef.current = onConfirm;
    setPreviewState({ isOpen: true, imageUrl });
  }, []);

  const runAnimateConfirm = useCallback(async (onConfirm, prompt) => {
    try {
      await onConfirm(prompt);
    } catch (error) {
      console.error(
        "[useAnimateImageWithPreview] animate confirm failed:",
        error
      );
    }
  }, []);

  const requestAnimateImage = useCallback(
    ({ imageUrl, onConfirm }) => {
      if (!imageUrl || !onConfirm) return;

      const { agencyID } = dispatch(getPathDatabaseByRole());
      if (isAgencyWithImageAnimationPromptModal(agencyID)) {
        openAnimatePreview({ imageUrl, onConfirm });
        return;
      }

      void runAnimateConfirm(onConfirm, "");
    },
    [dispatch, openAnimatePreview, runAnimateConfirm]
  );

  const cancelAnimate = useCallback(() => {
    setPreviewState({ isOpen: false, imageUrl: null });
    onConfirmRef.current = null;
  }, []);

  const confirmAnimate = useCallback(
    async (prompt) => {
      const handler = onConfirmRef.current;
      if (!handler) return;

      onConfirmRef.current = null;
      setPreviewState({ isOpen: false, imageUrl: null });

      await runAnimateConfirm(handler, prompt);
    },
    [runAnimateConfirm]
  );

  return {
    requestAnimateImage,
    openAnimatePreview,
    cancelAnimate,
    confirmAnimate,
    previewModalProps: {
      isOpen: previewState.isOpen,
      imageUrl: previewState.imageUrl,
      onClose: cancelAnimate,
      onConfirm: confirmAnimate,
      isSubmitting: false,
    },
  };
};
